import { forEach, isFunction, split } from 'es-toolkit/compat';
import { useCallback, useEffect, useState } from 'react';
import { useQueryParam } from 'use-query-params';

export const statelessUpdateUrlParams = (stringifiedUpdates: Record<string, string>) => {
  // Get the current hash (without the leading #)
  const currentHash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  // Parse the current hash into path and query string
  const [path, queryString] = split(currentHash, '?');
  // Parse existing query params
  const params = new URLSearchParams(queryString || '');
  // Update the relevant viewerStates in the query params
  forEach(stringifiedUpdates, (value, key) => {
    params.set(key, value);
  });
  // Rebuild the hash
  const newHash = path + (params.toString() ? `?${params.toString()}` : '');
  // Use history.replaceState to update the hash without a full rerender
  window.history.replaceState(null, '', `#${newHash}`);
};

const getCurrentHashQueryParam = (key: string): string | null => {
  const currentHash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  const [, queryString] = split(currentHash, '?');
  return new URLSearchParams(queryString || '').get(key);
};

/**
 * Initialize a local state from a query param, and sync the state with the URL in a stateless manner (i.e. without causing a full rerender).
 * This is useful for cases where you want to sync a local state with a query param, but you don't want to cause a full rerender when the state changes
 * (i.e. when the url param isn't used to share the state with other components).
 * @param props - The parameters to use for the query param
 * @returns A tuple of the local state and a function to update the state, which will also update the URL.
 */
export const useQueryParamAsLocalState = <TypeToEncode>(
  ...props: Parameters<typeof useQueryParam<TypeToEncode>>
): ReturnType<typeof useQueryParam<TypeToEncode>> => {
  const urlKey = props[0];
  const queryParamConfig = props[1];
  const [queryParam, setQueryParamUrlState] = useQueryParam<TypeToEncode>(...props);
  const [queryParamLocalState, setQueryParamLocalState] = useState<TypeToEncode>(queryParam);

  useEffect(() => {
    setQueryParamLocalState(queryParam);
  }, [queryParam, urlKey]);

  const setQueryParamStateless = useCallback(
    (...setQueryParamProps: Parameters<typeof setQueryParamUrlState>) => {
      const updates = setQueryParamProps[0];
      const updateUrl = (latestValue: TypeToEncode) => {
        const encodedValue = queryParamConfig.encode(latestValue);
        if (encodedValue !== null && encodedValue !== undefined) {
          statelessUpdateUrlParams({
            [urlKey]: Array.isArray(encodedValue) ? encodedValue.join(',') : encodedValue,
          });
        }
      };
      const latestValue = isFunction(updates)
        ? updates(queryParamConfig.decode(getCurrentHashQueryParam(urlKey)) as TypeToEncode)
        : updates;
      setQueryParamLocalState(latestValue);
      updateUrl(latestValue);
    },
    [queryParamConfig, urlKey]
  );
  return [queryParamLocalState, setQueryParamStateless];
};
